/**
 * Guest (Try as Guest) API – unauthenticated flow.
 * Uses student service base URL; sends X-Guest-Id header where required.
 */
import type { StaticQuizDTO } from './quizService';
import studentApi from './studentApi';

const GUEST_PREFIX = '/api/guest';

export interface GuestSessionResponse {
  guestId: string;
}

export interface GuestCourseTypeDTO {
  courseType: string;
  displayName: string;
  status: 'ACTIVE' | 'COMING_SOON' | 'INACTIVE' | 'ARCHIVED';
}

export type GuestEntryMethod = 'SEARCH' | 'BY_LOCATION' | 'QUICK_COURSE';

export interface GuestRegisterCourseRequest {
  guestId?: string;
  courseType?: string;
  courseId?: number;
  entryMethod?: GuestEntryMethod;
  countryId?: number;
  councilId?: number;
  schoolId?: number;
}

export interface CourseDTO {
  id: number;
  name: string;
  description: string;
  courseType: string;
  status: string;
  maxStudents?: number;
  durationWeeks?: number;
  difficultyLevel?: string;
  prerequisites?: string;
  curriculumAlignment?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GuestRegisterCourseResponse {
  courseId: number;
  course: CourseDTO;
  allowedTopicIds: number[];
}

export interface CourseTopicDTO {
  topicId: number;
  topicTitle: string;
  topicDescription?: string;
  status?: string;
  progress?: number;
  estimatedTimeMinutes?: number;
  hasQuiz?: boolean;
  hasAssignment?: boolean;
  assignmentSubmitted?: boolean;
  /** True when the guest may open this topic (up to 2 per session). */
  guestAccessible?: boolean;
}

export interface GuestCourseBrowseDTO {
  id: number;
  name: string;
  description?: string;
  courseType: string;
  status: string;
  board?: string;
  curriculumAlignment?: string;
  countryLabel?: string;
  topicCount: number;
}

export interface GuestTopicSearchResultDTO {
  topicId: number;
  title: string;
  description?: string;
  courseName?: string;
}

export interface GuestTopicDetailDTO {
  topicId: number;
  title: string;
  description?: string;
  content?: string;
  learningObjectives?: string;
  estimatedTimeMinutes?: number;
  difficultyLevel?: string;
  courseId?: number;
  courseName?: string;
}

/** Create or resolve guest session. Returns guestId. */
export async function createGuestSession(params?: { countryCode?: string; email?: string }): Promise<GuestSessionResponse> {
  const response = await studentApi.post<{ data: GuestSessionResponse }>(`${GUEST_PREFIX}/session`, params ?? {});
  const data = response.data?.data ?? response.data;
  return data;
}

/** Get guest session (e.g. GET with optional query params). */
export async function getGuestSession(params?: { countryCode?: string; email?: string }): Promise<GuestSessionResponse> {
  const search = params ? new URLSearchParams(params as Record<string, string>).toString() : '';
  const url = search ? `${GUEST_PREFIX}/session?${search}` : `${GUEST_PREFIX}/session`;
  const response = await studentApi.get<{ data: GuestSessionResponse }>(url);
  const data = response.data?.data ?? response.data;
  return data;
}

/** Update guest email (optional). */
export async function updateGuestEmail(guestId: string, email: string): Promise<void> {
  await studentApi.patch(`${GUEST_PREFIX}/session`, { email }, { headers: { 'X-Guest-Id': guestId } });
}

/** Get course types (active/coming-soon). No auth. */
export async function getGuestCourseTypes(): Promise<GuestCourseTypeDTO[]> {
  const response = await studentApi.get<{ data: GuestCourseTypeDTO[] }>(`${GUEST_PREFIX}/course-types`);
  return response.data?.data ?? response.data ?? [];
}

/** List ACTIVE courses for a course type (quick-try picker). No auth. */
export async function getGuestCoursesByType(courseType: string): Promise<GuestCourseBrowseDTO[]> {
  const response = await studentApi.get<{ data: GuestCourseBrowseDTO[] }>(`${GUEST_PREFIX}/courses`, {
    params: { courseType },
  });
  return response.data?.data ?? response.data ?? [];
}

/** Get courses by school (for browse-by-location). No auth. */
export async function getGuestCoursesBySchoolId(schoolId: number): Promise<CourseDTO[]> {
  const response = await studentApi.get<{ data: CourseDTO[] }>(
    `${GUEST_PREFIX}/courses/by-location`,
    { params: { schoolId } }
  );
  return response.data?.data ?? response.data ?? [];
}

/** Register guest for a course. Returns course and allowed topic IDs (max 2). */
export async function registerGuestCourse(
  guestId: string,
  body: {
    courseType?: string;
    courseId?: number;
    entryMethod?: GuestEntryMethod;
    countryId?: number;
    councilId?: number;
    schoolId?: number;
  }
): Promise<GuestRegisterCourseResponse> {
  const response = await studentApi.post<{ data: GuestRegisterCourseResponse }>(
    `${GUEST_PREFIX}/register-course`,
    { ...body, guestId },
    { headers: { 'X-Guest-Id': guestId } }
  );
  return response.data?.data ?? response.data;
}

/** Get course details for a course the guest is registered for. */
export async function getGuestCourse(guestId: string, courseId: number): Promise<CourseDTO> {
  const response = await studentApi.get<{ data: CourseDTO }>(`${GUEST_PREFIX}/courses/${courseId}`, {
    headers: { 'X-Guest-Id': guestId },
  });
  return response.data?.data ?? response.data;
}

/** Get only the up-to-2 topics the guest is allowed for the course. */
export async function getGuestCourseTopics(guestId: string, courseId: number): Promise<CourseTopicDTO[]> {
  const response = await studentApi.get<{ data: CourseTopicDTO[] }>(`${GUEST_PREFIX}/courses/${courseId}/topics`, {
    headers: { 'X-Guest-Id': guestId },
  });
  return response.data?.data ?? response.data ?? [];
}

/** Search topics for guest (Option 1). No auth. */
export async function searchGuestTopics(params: {
  q?: string;
  subjectId?: number;
}): Promise<GuestTopicSearchResultDTO[]> {
  const search = new URLSearchParams();
  if (params.q != null && params.q !== '') search.set('q', params.q);
  if (params.subjectId != null) search.set('subjectId', String(params.subjectId));
  const url = search.toString() ? `${GUEST_PREFIX}/search/topics?${search}` : `${GUEST_PREFIX}/search/topics`;
  const response = await studentApi.get<{ data: GuestTopicSearchResultDTO[] }>(url);
  return response.data?.data ?? response.data ?? [];
}

/** Get single topic for guest (Option 1 single-topic view). Requires guestId. */
export async function getGuestTopic(guestId: string, topicId: number): Promise<GuestTopicDetailDTO> {
  const response = await studentApi.get<{ data: GuestTopicDetailDTO }>(
    `${GUEST_PREFIX}/topics/${topicId}`,
    { headers: { 'X-Guest-Id': guestId } }
  );
  return response.data?.data ?? response.data;
}

/** Get published quiz(zes) for topic (guest). No JWT; uses X-Guest-Id. Returns list; use first for practice. */
export async function getGuestTopicQuiz(guestId: string, topicId: number): Promise<StaticQuizDTO[]> {
  const response = await studentApi.get<{ data: StaticQuizDTO[] }>(
    `${GUEST_PREFIX}/topics/${topicId}/quiz`,
    { headers: { 'X-Guest-Id': guestId } }
  );
  return response.data?.data ?? response.data ?? [];
}

/** Get topic content for guest (rich text). Use when topic detail does not include content. */
export async function getGuestTopicContent(guestId: string, topicId: number): Promise<string | null> {
  try {
    const response = await studentApi.get<{ data?: { content?: string } }>(
      `${GUEST_PREFIX}/topics/${topicId}/content`,
      { headers: { 'X-Guest-Id': guestId } }
    );
    return response.data?.data?.content ?? null;
  } catch (_) {
    return null;
  }
}

/** Submit a guest quiz attempt. Requires X-Guest-Id (sent by studentApi interceptor when guestId in localStorage). */
export async function submitGuestQuizAttempt(submission: {
  quizId: number;
  topicId: number;
  answers: Record<string, unknown>;
  score: number;
  correctAnswers: number;
  timeTakenMinutes: number;
  passed: boolean;
}): Promise<{ id: number; quizId: number; topicId: number; score: number; totalQuestions: number; correctAnswers: number; passed: boolean; status: string; startedAt: string; completedAt: string }> {
  const response = await studentApi.post<{ data: typeof submission }>(
    `${GUEST_PREFIX}/quiz/attempt`,
    submission
  );
  return response.data?.data ?? response.data;
}

/** Get published assessments for a topic (guest). */
export async function getGuestAssessmentsByTopic(guestId: string, topicId: number): Promise<AssessmentDTO[]> {
  const response = await studentApi.get<{ data: AssessmentDTO[] }>(
    `${GUEST_PREFIX}/topics/${topicId}/assessments`,
    { headers: { 'X-Guest-Id': guestId } }
  );
  return response.data?.data ?? response.data ?? [];
}

/** Start a guest assessment attempt. */
export async function startGuestAssessment(guestId: string, assessmentId: number): Promise<AssessmentAttemptDTO> {
  const response = await studentApi.post<{ data: AssessmentAttemptDTO }>(
    `${GUEST_PREFIX}/assessments/${assessmentId}/start`,
    null,
    { headers: { 'X-Guest-Id': guestId } }
  );
  return response.data?.data ?? response.data;
}

/** Submit a guest assessment attempt. */
export async function submitGuestAssessmentAttempt(
  guestId: string,
  attemptId: number,
  submission: { answers?: Record<string, unknown>; workFileUrls?: string[] }
): Promise<AssessmentAttemptDTO> {
  const response = await studentApi.post<{ data: AssessmentAttemptDTO }>(
    `${GUEST_PREFIX}/assessments/attempts/${attemptId}/submit`,
    submission,
    { headers: { 'X-Guest-Id': guestId } }
  );
  return response.data?.data ?? response.data;
}

/** List guest assessment attempts. */
export async function getGuestAssessmentAttempts(guestId: string): Promise<AssessmentAttemptDTO[]> {
  const response = await studentApi.get<{ data: AssessmentAttemptDTO[] }>(
    `${GUEST_PREFIX}/assessments/attempts`,
    { headers: { 'X-Guest-Id': guestId } }
  );
  return response.data?.data ?? response.data ?? [];
}

export interface AssessmentDTO {
  id: number;
  title: string;
  description?: string;
  courseId: number;
  status: string;
  topicIds: number[];
  passingScore?: number;
  maxAttempts?: number;
  durationMinutes?: number;
  scheduledDate?: string;
  dueDate?: string;
}

export interface AssessmentAttemptDTO {
  id: number;
  assessmentId: number;
  assessmentTitle?: string;
  topicIds?: number[];
  startedAt: string;
  submittedAt?: string;
  status: string;
  score?: number;
  correctAnswers?: number;
  totalQuestions?: number;
  timeTakenMinutes?: number;
  passed?: boolean;
  answers?: Record<string, unknown>;
}

/** Record that the guest opened a topic (rejects if already 2 topics). */
export async function recordGuestTopicAccess(
  guestId: string,
  topicId: number,
  courseId: number
): Promise<void> {
  await studentApi.post(
    `${GUEST_PREFIX}/topics/access`,
    null,
    { params: { topicId, courseId }, headers: { 'X-Guest-Id': guestId } }
  );
}
